import { FormField } from '@shared/components/add-form/add-form.component';
import {
  ConnectProviderRequestDTO,
  LogisticsProviderAccountDTO,
} from '@app/api/after-sales-service';
import { CARRIER_CODE } from '../carrier.config';

export type CarrierFormMode = 'add' | 'edit' | 'view';

export function resolveCarrierFormModeFromUrl(url: string): CarrierFormMode {
  if (url.includes('/view/')) return 'view';
  if (url.includes('/edit/')) return 'edit';
  return 'add';
}

export function resolveCarrierFormTitleKey(mode: CarrierFormMode): string {
  if (mode === 'view') return 'carrier.form.title_view';
  if (mode === 'edit') return 'carrier.form.title_edit';
  return 'carrier.form.title_add';
}

export function parseCarrierIdParam(id: string | null): number | null {
  if (!id) return null;
  const parsed = Number.parseInt(id, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function mapCarrierToFormData(
  carrier: LogisticsProviderAccountDTO,
): Record<string, unknown> {
  const formData: Record<string, unknown> = {
    providerCode: carrier.providerCode,
    accountCode: carrier.code,
    accountName: carrier.name,
    description: carrier.description,
    status: carrier.status === 'ACTIVE',
    webhookSecret: carrier.webhookSecret ?? '',
  };

  if (carrier.providerCode === CARRIER_CODE.VTP && carrier.credentials) {
    const credentials = carrier.credentials as Record<string, unknown>;
    const username = credentials['username'];
    if (typeof username === 'string' && username.trim()) {
      formData['vtp_username'] = username;
    }
  }

  return formData;
}

export function buildCarrierPayload(
  formData: Record<string, unknown>,
): ConnectProviderRequestDTO {
  const providerCode = String(formData['providerCode'] ?? '');
  const connectInfo: Record<string, object> = {};

  if (providerCode === CARRIER_CODE.VTP) {
    const username = String(formData['vtp_username'] ?? '').trim();
    const password = String(formData['vtp_password'] ?? '').trim();
    if (username) connectInfo['username'] = username as unknown as object;
    if (password) connectInfo['password'] = password as unknown as object;
  }

  return {
    providerCode:
      providerCode as ConnectProviderRequestDTO.ProviderCodeEnum,
    code: String(formData['accountCode'] ?? '').trim() || undefined,
    name: String(formData['accountName'] ?? '').trim() || undefined,
    description: String(formData['description'] ?? '').trim() || undefined,
    status: formData['status'] ? 'ACTIVE' : 'INACTIVE',
    connectInfo: Object.keys(connectInfo).length > 0 ? connectInfo : undefined,
  };
}

function hideField(field: FormField): void {
  field.hidden = true;
  field.validators = [];
  field.required = false;
}

export function updateCarrierConnectionFieldVisibility(
  fields: FormField[],
  providerCode: string | null | undefined,
  viewMode: boolean,
  editMode: boolean,
): void {
  fields.forEach((field) => {
    if (field.section !== 'carrier.form.sections.connection_info') return;

    if (!providerCode) {
      hideField(field);
      return;
    }

    if (providerCode !== CARRIER_CODE.VTP) {
      hideField(field);
      return;
    }

    if (field.controlName === 'vtp_username') {
      field.hidden = false;
      return;
    }

    if (field.controlName === 'vtp_password') {
      field.hidden = viewMode || editMode;
      if (field.hidden) {
        field.validators = [];
        field.required = false;
      }
      return;
    }

    if (field.controlName === 'webhookSecret') {
      field.hidden = !viewMode && !editMode;
      if (field.hidden) {
        field.validators = [];
        field.required = false;
      }
      return;
    }

    hideField(field);
  });
}

export function updateCarrierReadonlyFields(
  fields: FormField[],
  editMode: boolean,
): void {
  if (!editMode) return;
  fields.forEach((field) => {
    if (
      field.controlName === 'providerCode' ||
      field.controlName === 'accountCode' ||
      field.controlName === 'vtp_username'
    ) {
      field.readonly = true;
    }
  });
}
